'use client'

import * as React from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { IconChevronRight, IconLogOut } from './icons'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

/**
 * Etz vertical navigation — Figma "Navigation" page:
 * Sidebar (510:1189) · Nav Item (508:116) · Nav Sub Item (508:1067) ·
 * Nav Group Label (508:1068) · Sidebar User Card (508:1086).
 * Anatomy mirrors shadcn/ui's Sidebar: Provider owns collapse state,
 * Header/Content/Group/NavItem/UserCard compose inside the shell.
 * Collapse contract: localStorage "sidebar-collapsed" + data-sidebar-collapsed
 * on <html>; globals.css maps that to --sidebar-width (240px / 64px), which
 * the main content area reads for its left offset.
 */

type SidebarContextValue = {
    collapsed: boolean
    setCollapsed: (collapsed: boolean) => void
    toggle: () => void
}

const SidebarContext = React.createContext<SidebarContextValue>({
    collapsed: false,
    setCollapsed: () => {},
    toggle: () => {},
})

export function useSidebar() {
    return React.useContext(SidebarContext)
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
    const [collapsed, setCollapsed] = React.useState(false)

    React.useEffect(() => {
        const stored = localStorage.getItem('sidebar-collapsed')
        if (stored) setCollapsed(stored === 'true')
    }, [])

    React.useEffect(() => {
        localStorage.setItem('sidebar-collapsed', String(collapsed))
        // globals.css maps this attribute to --sidebar-width inside the lg media
        // query, so mobile keeps --sidebar-width: 0 (an inline style would not)
        document.documentElement.setAttribute('data-sidebar-collapsed', String(collapsed))
    }, [collapsed])

    const value = React.useMemo(
        () => ({ collapsed, setCollapsed, toggle: () => setCollapsed((prev) => !prev) }),
        [collapsed]
    )

    return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

export function Sidebar({ className, children, ...props }: React.ComponentProps<'aside'>) {
    const { collapsed } = useSidebar()
    return (
        <aside
            data-collapsed={collapsed}
            className={cn(
                // h-dvh, not h-screen: iPad-landscape Safari shows this rail, and
                // 100vh would put its bottom pinned items behind the browser toolbar.
                'fixed left-0 top-0 z-40 hidden h-dvh flex-col gap-3 border-r border-[var(--border-subtle)] bg-[var(--bg-subtle)] transition-all duration-300 lg:flex',
                collapsed ? 'w-16 items-center pb-4 pt-3' : 'w-60 px-4 py-3',
                className
            )}
            {...props}
        >
            <TooltipProvider delayDuration={300}>{children}</TooltipProvider>
        </aside>
    )
}

export function SidebarHeader({ className, children }: { className?: string; children: React.ReactNode }) {
    const { collapsed } = useSidebar()
    return (
        <div
            className={cn(
                // Deliberately rule-free (2026-08-04, Lovable direction): the
                // topbar's border-b is the only horizontal line up top — a
                // second line on the rail fought it at every height.
                'flex shrink-0',
                collapsed ? 'flex-col items-center gap-2' : 'w-full items-center gap-2.5 py-1 pl-1',
                className
            )}
        >
            {children}
        </div>
    )
}

export function SidebarCollapseToggle({ className }: { className?: string }) {
    const { collapsed, toggle } = useSidebar()
    return (
        <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className={cn(
                // 28px visual control per Figma; ::before extends the hit area to 44px
                'relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--icon-tertiary)] transition-colors',
                'before:absolute before:-inset-2 before:content-[""]',
                'hover:bg-[var(--bg-subtle-hover)] hover:text-[var(--icon-secondary)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]',
                className
            )}
        >
            <IconChevronRight className={cn(!collapsed && 'rotate-180')} />
        </button>
    )
}

export function SidebarDivider({ className }: { className?: string }) {
    const { collapsed } = useSidebar()
    return (
        <div
            className={cn('h-px shrink-0 bg-[var(--border-subtle)]', collapsed ? 'w-8' : 'w-full', className)}
            aria-hidden="true"
        />
    )
}

export function SidebarContent({ className, children }: { className?: string; children: React.ReactNode }) {
    const { collapsed } = useSidebar()
    return (
        <div
            className={cn(
                'flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden',
                collapsed ? 'items-center gap-1' : 'w-full gap-5',
                className
            )}
        >
            {children}
        </div>
    )
}

export function SidebarGroup({ label, className, children }: { label?: string; className?: string; children: React.ReactNode }) {
    const { collapsed } = useSidebar()
    return (
        <div className={cn('flex shrink-0 flex-col gap-1', collapsed ? 'items-center' : 'w-full', className)}>
            {label && !collapsed && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
            {children}
        </div>
    )
}

export function SidebarGroupLabel({ className, children }: { className?: string; children: React.ReactNode }) {
    return (
        <p
            className={cn(
                'px-3 pb-1 pt-2 text-xs font-semibold uppercase leading-4 tracking-[0.08em] text-[var(--text-tertiary)]',
                className
            )}
        >
            {children}
        </p>
    )
}

export function SidebarNavBadge({ children }: { children: React.ReactNode }) {
    return (
        <span className="shrink-0 rounded-full bg-[var(--bg-brand-subtle)] px-2 py-0.5 text-xs font-medium text-[var(--text-brand)]">
            {children}
        </span>
    )
}

type SidebarNavItemProps = {
    label: string
    icon: React.ReactNode
    href?: string
    active?: boolean
    disabled?: boolean
    showBadge?: boolean
    badgeCount?: string
    /** Unread signal: 8px brand dot after the label (collapsed: corner dot on the icon). */
    showDot?: boolean
    onClick?: () => void
    /** Force expanded rendering regardless of context (mobile drawer). */
    forceExpanded?: boolean
    className?: string
    'data-tour'?: string
}

/**
 * Nav Item (508:116). Font weight never changes between states so the active
 * state causes no layout shift. Focus ring is inset per the Figma inside-ring.
 */
export function SidebarNavItem({
    label,
    icon,
    href,
    active = false,
    disabled = false,
    showBadge = false,
    badgeCount,
    showDot = false,
    onClick,
    forceExpanded = false,
    className,
    ...rest
}: SidebarNavItemProps) {
    const { collapsed: contextCollapsed } = useSidebar()
    const collapsed = forceExpanded ? false : contextCollapsed

    const itemClasses = cn(
        'group flex shrink-0 items-center rounded-md transition-colors duration-200',
        collapsed ? 'h-11 w-11 justify-center' : 'h-11 w-full gap-3 px-3',
        // reskin 2026-07-31: active = white pill lifted off the tinted rail
        active && 'bg-[var(--bg-surface)] ring-1 ring-[var(--border-subtle)]',
        !active && !disabled && 'hover:bg-[var(--bg-subtle-hover)]',
        disabled && 'pointer-events-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]',
        className
    )

    const iconClasses = cn(
        'flex size-5 shrink-0 items-center justify-center transition-colors',
        active
            ? 'text-[var(--icon-brand)]'
            : disabled
              ? 'text-[var(--icon-tertiary)]'
              : 'text-[var(--icon-secondary)] group-hover:text-[var(--icon-primary)] group-focus-visible:text-[var(--icon-primary)]'
    )

    const labelClasses = cn(
        // Long labels ('Members & households') wrap to a second line inside the
        // 44px row rather than truncating — a nav label must never be cut off.
        'min-w-0 flex-1 text-left text-sm font-medium leading-5 transition-colors [overflow-wrap:anywhere] line-clamp-2',
        active
            ? 'text-[var(--text-brand)]'
            : disabled
              ? 'text-[var(--text-disabled)]'
              : 'text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] group-focus-visible:text-[var(--text-primary)]'
    )

    const content = (
        <>
            <span className={cn(iconClasses, collapsed && (showBadge || showDot) && 'relative')} aria-hidden="true">
                {icon}
                {/* Collapsed rail keeps the queue/unread signal as a dot; the
                    tooltip carries the count */}
                {collapsed && (showBadge || showDot) && (
                    <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[var(--primary-600)]" />
                )}
            </span>
            {!collapsed && <span className={labelClasses}>{label}</span>}
            {!collapsed && showDot && (
                <>
                    <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-[var(--bg-brand)]" />
                    <span className="sr-only">Unread updates</span>
                </>
            )}
            {!collapsed && showBadge && <SidebarNavBadge>{badgeCount}</SidebarNavBadge>}
        </>
    )

    // Collapsed items carry their name via aria-label, which overrides inner
    // sr-only text — so the unread signal has to ride along in the label.
    const collapsedLabel = showDot ? `${label}, unread updates` : label

    const item = href ? (
        <Link
            href={href}
            aria-current={active ? 'page' : undefined}
            aria-disabled={disabled || undefined}
            aria-label={collapsed ? collapsedLabel : undefined}
            tabIndex={disabled ? -1 : undefined}
            className={itemClasses}
            {...rest}
        >
            {content}
        </Link>
    ) : (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={collapsed ? collapsedLabel : undefined}
            className={itemClasses}
            {...rest}
        >
            {content}
        </button>
    )

    if (!collapsed) return item

    return (
        <Tooltip>
            <TooltipTrigger asChild>{item}</TooltipTrigger>
            <TooltipContent side="right">
                {showBadge && badgeCount ? `${label} (${badgeCount})` : label}
            </TooltipContent>
        </Tooltip>
    )
}

type SidebarNavSubItemProps = {
    label: string
    href: string
    active?: boolean
    className?: string
}

/**
 * Nav Sub Item (508:1067) — 40px row, left padding aligns with the parent
 * label start (44px), no icon. Built for future nesting; not rendered when
 * the rail is collapsed.
 */
export function SidebarNavSubItem({ label, href, active = false, className }: SidebarNavSubItemProps) {
    const { collapsed } = useSidebar()
    if (collapsed) return null
    return (
        <Link
            href={href}
            aria-current={active ? 'page' : undefined}
            className={cn(
                'group flex h-10 w-full shrink-0 items-center rounded-md pl-11 pr-3 transition-colors duration-200',
                active ? 'bg-[var(--bg-surface)] ring-1 ring-[var(--border-subtle)]' : 'hover:bg-[var(--bg-subtle-hover)]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]',
                className
            )}
        >
            <span
                className={cn(
                    'min-w-0 flex-1 truncate text-sm font-medium leading-5 transition-colors',
                    active
                        ? 'text-[var(--text-brand)]'
                        : 'text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] group-focus-visible:text-[var(--text-primary)]'
                )}
            >
                {label}
            </span>
        </Link>
    )
}

type SidebarUserCardProps = React.ComponentPropsWithoutRef<'button'> & {
    name: string
    role: string
    initials: string
    imageUrl?: string | null
}

/**
 * Sidebar User Card (508:1086). The whole card is one button (dropdown
 * trigger); the trailing LogOut glyph signals the exit but is decorative.
 * Collapsed: the 32px initials avatar inside a 44px target, with a tooltip.
 */
export const SidebarUserCard = React.forwardRef<HTMLButtonElement, SidebarUserCardProps>(
    function SidebarUserCard({ name, role, initials, imageUrl, className, ...props }, ref) {
        const { collapsed } = useSidebar()

        const avatar = imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- tenant-uploaded avatar, remote host unknown
            <img src={imageUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" />
        ) : (
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--bg-brand-subtle)] text-xs font-semibold text-[var(--text-brand)]">
                {initials}
            </span>
        )

        const button = (
            <button
                ref={ref}
                type="button"
                aria-label={collapsed ? `Account menu for ${name}` : undefined}
                className={cn(
                    'flex shrink-0 items-center rounded-md text-left transition-colors',
                    collapsed ? 'h-11 w-11 justify-center' : 'h-14 w-full gap-3 pl-2 pr-3',
                    'hover:bg-[var(--bg-subtle-hover)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]',
                    className
                )}
                {...props}
            >
                {avatar}
                {!collapsed && (
                    <>
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium leading-5 text-[var(--text-primary)]">
                                {name}
                            </span>
                            <span className="block truncate text-xs leading-4 text-[var(--text-tertiary)]">
                                {role}
                            </span>
                        </span>
                        <span className="shrink-0 text-[var(--icon-tertiary)]" aria-hidden="true">
                            <IconLogOut size={16} />
                        </span>
                    </>
                )}
            </button>
        )

        if (!collapsed) return button

        return (
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent side="right">{name}</TooltipContent>
            </Tooltip>
        )
    }
)
